// ThemeContext.tsx
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from '../theme';
import api from '@/services/api';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

type ThemeMode = 'light' | 'dark';

interface ThemeContextProps {
  toggleTheme: () => void;
  setMode: (mode: ThemeMode) => void;
  mode: ThemeMode;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context)
    throw new Error('useThemeMode deve ser usado dentro do ThemeProvider');
  return context;
};

// Agora o tema padrão é 'dark' se nada estiver salvo no localStorage
const getInitialMode = (): ThemeMode => {
  const storedMode = localStorage.getItem('theme') as ThemeMode | null;
  return storedMode ?? 'dark';
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(getInitialMode);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const setMode = async (newMode: ThemeMode) => {
    localStorage.setItem('theme', newMode);
    setModeState(newMode);
    try {
      const response = await api.patch(
        `/user-preferences?theme=${newMode.toUpperCase()}`
      );
      if (response.status === 200)
        enqueueSnackbar(t('contexts.theme.successSave'), {
          variant: 'success',
        });
    } catch {
      enqueueSnackbar(t('contexts.theme.failedSave'), { variant: 'error' });
    }
  };

  const toggleTheme = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
  };

  const theme = useMemo(
    () => (mode === 'light' ? lightTheme : darkTheme),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ toggleTheme, setMode, mode }}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
